import { useEffect, useMemo, useState } from 'react'
import { format } from 'date-fns'
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { paperAPI } from '../../services/api'
import type { PaperAccountSummary, PaperEquityCurve } from '../../types'
import { niceScale, useChartTheme } from './chartTheme'
import TerminalPanel from './TerminalPanel'

export const EQUITY_POLL_MS = 300_000

const money = (value: number) =>
  value.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })

const moneyExact = (value: number) =>
  value.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })

interface TooltipPayload {
  active?: boolean
  payload?: { payload: { time: number; equity: number; cash: number; open_positions: number; regime_on: boolean } }[]
}

const EquityTooltip = ({ active, payload }: TooltipPayload) => {
  const point = active && payload?.length ? payload[0].payload : null
  if (!point) return null
  return (
    <div className="rounded-md border border-terminal-border bg-terminal-raised px-3 py-2 text-xs shadow-lg">
      <p className="font-mono text-gray-400">{format(point.time, 'MMM d, yyyy')}</p>
      <p className="mt-1 font-mono text-sm font-semibold tabular-nums text-gray-50">{moneyExact(point.equity)}</p>
      <p className="mt-0.5 font-mono text-[11px] tabular-nums text-gray-500">
        {point.open_positions} open · {moneyExact(point.cash)} cash
      </p>
      <p className={`mt-0.5 text-[11px] ${point.regime_on ? 'text-emerald-400' : 'text-amber-400'}`}>
        BTC filter {point.regime_on ? 'on' : 'off'}
      </p>
    </div>
  )
}

/**
 * The paper account's equity curve against its $10,000 starting line, in the
 * live price chart's style. The kill rule sits on the panel because it is the
 * thing that decides whether this curve keeps existing.
 */
const PaperEquityChart = ({ account }: { account: PaperAccountSummary | null }) => {
  const chart = useChartTheme()
  const [curve, setCurve] = useState<PaperEquityCurve | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let active = true
    const load = async () => {
      try {
        const data = await paperAPI.equityCurve()
        if (!active) return
        setCurve(data)
        setFailed(false)
      } catch (error) {
        if (!active) return
        console.error('Paper equity curve refresh failed:', error)
        setFailed(true)
      }
    }
    load()
    const timer = setInterval(load, EQUITY_POLL_MS)
    return () => {
      active = false
      clearInterval(timer)
    }
  }, [])

  const start = curve?.starting_balance ?? account?.starting_balance ?? 10000

  const points = useMemo(
    () =>
      (curve?.points ?? []).map((point) => ({
        time: new Date(`${point.as_of}T00:00:00Z`).getTime(),
        equity: point.equity,
        cash: point.cash,
        open_positions: point.open_positions,
        regime_on: point.regime_on,
      })),
    [curve]
  )

  const last = points.length ? points[points.length - 1].equity : start
  const direction = last > start ? 'up' : last < start ? 'down' : 'flat'
  const color = chart.colors[direction]
  const yScale = niceScale([...points.map((p) => p.equity), start])

  const killed = account?.status === 'killed' || account?.status === 'killing'
  const notStarted = account?.status === 'not_started'

  return (
    <TerminalPanel
      title="Paper equity curve"
      tone={killed ? 'red' : direction === 'up' ? 'green' : direction === 'down' ? 'red' : 'slate'}
      subtitle={
        <p className="font-mono text-xs tabular-nums text-gray-500" data-testid="paper-equity-subtitle">
          {money(last)} · started {money(start)}
          {account && !notStarted ? ` · day ${account.day} of ~${account.eval_days}` : ''}
        </p>
      }
      action={
        account && (
          <div
            className={`rounded px-2 py-1 text-[10px] font-semibold uppercase tracking-wider ring-1 ring-inset ${
              killed
                ? 'bg-red-500/10 text-red-300 ring-red-500/30'
                : 'bg-emerald-500/10 text-emerald-300 ring-emerald-500/30'
            }`}
            data-testid="paper-status-pill"
          >
            {account.status.replace('_', ' ')}
          </div>
        )
      }
    >
      <div className="h-64 md:h-80">
        {failed && !curve ? (
          <div className="flex h-full items-center justify-center text-sm text-gray-400">
            The paper equity curve is unavailable right now.
          </div>
        ) : points.length < 2 ? (
          <div className="flex h-full flex-col items-center justify-center gap-1 text-center">
            <p className="text-sm text-gray-300">
              {notStarted ? 'The forward test has not started yet' : 'Not enough history to plot yet'}
            </p>
            <p className="text-xs text-gray-500">
              {notStarted
                ? 'The first scheduled run, at 00:15 UTC, opens the account and starts the 180-day clock.'
                : 'The paper engine records one point per closed daily candle, starting the day after launch.'}
            </p>
          </div>
        ) : (
          <div
            role="img"
            aria-label={`Paper account equity: ${moneyExact(last)} against a ${money(start)} starting balance`}
            data-direction={direction}
            data-testid="paper-equity-chart"
            className="h-full"
          >
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={points} margin={{ top: 8, right: 0, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="paper-equity-fill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={color} stopOpacity={0.28} />
                    <stop offset="100%" stopColor={color} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={chart.grid} strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="time"
                  type="number"
                  scale="time"
                  domain={['dataMin', 'dataMax']}
                  tickFormatter={(time: number) => format(time, 'MMM d')}
                  tick={chart.axisTick}
                  tickLine={false}
                  axisLine={{ stroke: chart.grid }}
                  minTickGap={40}
                />
                <YAxis
                  orientation="right"
                  domain={yScale.domain}
                  ticks={yScale.ticks}
                  tickFormatter={(value: number) => money(value)}
                  tick={chart.axisTick}
                  tickLine={false}
                  axisLine={false}
                  width={72}
                />
                <ChartTooltip
                  content={<EquityTooltip />}
                  cursor={{ stroke: chart.cursor, strokeWidth: 1, strokeDasharray: '3 3' }}
                  isAnimationActive={false}
                />
                {/* Break-even: the line the kill rule measures trigger 1 against. */}
                <ReferenceLine
                  y={start}
                  stroke={chart.axisTick.fill}
                  strokeDasharray="4 4"
                  label={{ value: money(start), position: 'insideTopLeft', fill: chart.axisTick.fill, fontSize: 11 }}
                />
                <Area
                  type="linear"
                  dataKey="equity"
                  stroke={color}
                  strokeWidth={2}
                  fill="url(#paper-equity-fill)"
                  dot={false}
                  activeDot={{ r: 4, fill: color, stroke: chart.surface, strokeWidth: 2 }}
                  isAnimationActive={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {account && <KillRule account={account} />}
    </TerminalPanel>
  )
}

/** The pre-set stop conditions, shown with live progress toward each. */
const KillRule = ({ account }: { account: PaperAccountSummary }) => {
  const drawdownHit = account.drawdown_pct >= account.kill_max_drawdown_pct
  const breakevenArmed = account.closed_trades >= account.kill_min_trades
  const breakevenHit = breakevenArmed && account.current_balance < account.starting_balance

  return (
    <div
      className="mt-4 rounded-md border border-terminal-border bg-terminal-raised/40 p-3"
      data-testid="paper-kill-rule"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Kill rule — pre-set, no discretion</p>
        {account.kill_reason && (
          <p className="font-mono text-[11px] text-red-300" data-testid="paper-kill-reason">
            fired: {account.kill_reason}
          </p>
        )}
      </div>
      <ul className="mt-2 space-y-1.5 text-xs">
        <li className="flex items-baseline justify-between gap-3">
          <span className={drawdownHit ? 'text-red-300' : 'text-gray-400'}>
            Stop if drawdown exceeds {account.kill_max_drawdown_pct.toFixed(0)}%
          </span>
          <span
            className={`font-mono tabular-nums ${drawdownHit ? 'text-red-300' : 'text-gray-300'}`}
            data-testid="paper-kill-drawdown"
          >
            now {account.drawdown_pct.toFixed(1)}%
          </span>
        </li>
        <li className="flex items-baseline justify-between gap-3">
          <span className={breakevenHit ? 'text-red-300' : 'text-gray-400'}>
            Stop if below break-even after {account.kill_min_trades} closed trades
          </span>
          <span
            className={`font-mono tabular-nums ${breakevenHit ? 'text-red-300' : 'text-gray-300'}`}
            data-testid="paper-kill-breakeven"
          >
            {account.closed_trades}/{account.kill_min_trades} closed
            {breakevenArmed ? (account.current_balance < account.starting_balance ? ' · below' : ' · above') : ''}
          </span>
        </li>
      </ul>
    </div>
  )
}

export default PaperEquityChart
